import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  fetchFeaturedproducts as fetchFeaturedproductsFromServices,
  fetchProducts as fetchProductsFromService,
  fetchSingleProduct as fetchSingleProductFromService,
} from "../apiServices/productService";
import { useLanguage } from "./LanguageContext";
import { useLocation } from "react-router-dom";

export const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [name, setName] = useState(""); // Changed state from searchQuery to name
  const { language } = useLanguage();
  const limit = 6;
  const location = useLocation();

  const fetchProducts = useCallback(
    async (
      category_id = "",
      name = "",
      reset = false,
      isSearch = false,
      brand = ""
    ) => {
      if (!hasMore && !reset && !isSearch && !category_id && !brand) return;

      try {
        setLoading(true);

        const newPageValue = reset ? 1 : page;
        const newLimit = limit; // Ensure limit is always included
        const newName = isSearch ? name : "";

        const data = await fetchProductsFromService(
          language,
          newLimit, // Always include limit
          newPageValue, // Paginate results properly
          newName,
          isSearch,
          category_id,
          brand
        );

        if (reset) {
          setProducts(data);
          setPage(2);
        } else {
          setProducts((prev) => [...prev, ...data]);
          setPage((prev) => prev + 1);
        }

        setHasMore(data.length === limit);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Error fetching products. Please try again later.");
      } finally {
        setLoading(false);
      }
    },
    [language, page, hasMore]
  );

  const fetchSingleProduct = async (product_id, lang) => {
    try {
      setLoading(true);
      const productDetails = await fetchSingleProductFromService(
        product_id,
        lang
      );
      return productDetails;
    } catch (err) {
      console.error("Error fetching single product:", err);
      setError("Error fetching product. Please try again later.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedProducts = async (lang) => {
    try {
      setLoading(true);
      const products = await fetchFeaturedproductsFromServices(lang);
      setFeaturedProducts(products);
      return products;
    } catch (err) {
      console.error("Error fetching single product:", err);
      setError("Error fetching product. Please try again later.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "/products") {
      setProducts([]);
      setPage(1);
      setHasMore(true);
      fetchProducts("", name, true, false);
    }
  }, [location.pathname, name, language]);

  return (
    <ProductContext.Provider
      value={{
        products,
        loading,
        error,
        fetchProducts,
        fetchSingleProduct,
        fetchFeaturedProducts,
        featuredProducts,
        hasMore,
        setName, // Provide setName function to update the name
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => useContext(ProductContext);
